import React from 'react';
import PropTypes from 'prop-types';
import Container from './container';
import getTranslatedText from '../../../helper/get_translated_text';
import TranslationContainer from '../../translation/translation_container';

const SELECT_CLASS_NAME = 'floating-label-select';

function optionsToOptionNodes(option, index, unavailableOptions) {
    let [value, label] = option;
    let disableOption = false;
    if (Array.isArray(unavailableOptions) && unavailableOptions.length > 0) {
        for (let i = 0; i < unavailableOptions.length; i++) {
            if (unavailableOptions[i] === value) {
                disableOption = true;
                break;
            }
        }
    }

    if (disableOption) {
        return <option value={value} key={value + label + index} disabled="disabled"
            className="floating-label-select__select-option floating-label-select__select-option-disabled">{label}</option>;
    }

    if (Array.isArray(unavailableOptions) && unavailableOptions.length > 0) {
        return <option value={value} key={value + label + index}
            className="floating-label-select__select-option floating-label-select__select-option-enabled">{label}</option>;
    }

    return <option value={value} key={value + label + index}
        className="floating-label-select__select-option">{label}</option>;
}

function getOptionComponents(options, unavailableOptions) {
    if (!options.length) {
        return [];
    } else if (options.length === 1 && !Array.isArray(options[0])) {
        return options[0].values.map(optionsToOptionNodes);
    } else {
        return options.map((option, index) => {
            if (Array.isArray(option)) {
                return optionsToOptionNodes(option, index, unavailableOptions);
            } else {
                return (
                    <TranslationContainer translationKey={option.label} targetProperty="label" key={option.label}>
                        <optgroup>
                            {option.values.map(optionsToOptionNodes)}
                        </optgroup>
                    </TranslationContainer>
                );
            }
        });
    }
}

function Select(props) {
    const options = props.loading && !props.value ? [] : [...props.options];

    const selectClassNames = ['floating-label-select__select'];
    if (props.required) {
        selectClassNames.push('floating-label-select__select--required');
    } else if (props.provideAllOption) {
        options.unshift([
            props.allOptionsValue,
            getTranslatedText(
                props.provideAllTranslationKey ?
                    props.provideAllTranslationKey :
                    'floating-label-select-all-label'
            )
        ]);
    }

    // Add label to display label if select is empty
    options.unshift([props.allOptionsValue, props.label]);

    return (
        <Container
            name={props.name}
            label={props.label}
            hasValue={props.hasValue}
            highlight={props.highlight}
            disabled={props.disabled}
            loading={props.loading}
            hintText={props.hintText}
            errorText={props.errorText}
            invalid={props.invalid}
            additionalClassNames={[SELECT_CLASS_NAME].concat(props.additionalClassNames)}
        >
            <select
                name={props.name}
                className={selectClassNames.join(' ')}
                onChange={event => props.onChange(props.transformOnChangeValue(event.target.value), props.name, props)}
                value={props.value}
                disabled={props.disabled}
            >
                {getOptionComponents(options, props.unavailableOptions)}
            </select>
        </Container>
    );
}

Select.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    hasValue: PropTypes.bool.isRequired,
    options: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    highlight: PropTypes.bool,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    hintText: PropTypes.string,
    errorText: PropTypes.string,
    invalid: PropTypes.bool,
    provideAllTranslationKey: PropTypes.string,
    provideAllOption: PropTypes.bool,
    allOptionsValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    additionalClassNames: PropTypes.array,
    transformOnChangeValue: PropTypes.func,
    unavailableOptions: PropTypes.array,
};

Select.defaultProps = {
    highlight: false,
    disabled: false,
    loading: false,
    hintText: '',
    errorText: '',
    invalid: false,
    provideAllTranslationKey: '',
    provideAllOption: false,
    allOptionsValue: '',
    additionalClassNames: [],
    unavailableOptions: [],
    transformOnChangeValue: v => v,
};

export default Select;
